import { useCallback, useEffect, useState } from 'react';
import {
  RoleCode,
  type TotalJpMengajar,
  type PeriodeHonor,
  type PegawaiListItem,
} from '@app/shared';
import { AdminLayout } from '../components/admin-layout';
import {
  Alert,
  Button,
  Card,
  Input,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Modal,
} from '../components/ui';
import { useAuth } from '../features/auth/use-auth';
import {
  getTotalJpMengajarList,
  createTotalJpMengajar,
  updateTotalJpMengajar,
  deleteTotalJpMengajar,
} from '../features/honorarium/api';
import { getPeriodeHonorList } from '../features/honorarium/api';
import { getPegawaiList } from '../features/master/api';

type FormState = {
  pegawai_sekolah_id: string;
  periode_id: string;
  total_jp: string;
};

const emptyForm = (): FormState => ({
  pegawai_sekolah_id: '',
  periode_id: '',
  total_jp: '0',
});

export function TotalJpMengajarPage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const [rows, setRows] = useState<TotalJpMengajar[]>([]);
  const [pegawaiList, setPegawaiList] = useState<PegawaiListItem[]>([]);
  const [periodeList, setPeriodeList] = useState<PeriodeHonor[]>([]);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [filterPeriode, setFilterPeriode] = useState<string>('');
  const [editing, setEditing] = useState<TotalJpMengajar | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (page = 1) => {
      try {
        const [jpResult, pegawaiResult, periodeResult] = await Promise.all([
          getTotalJpMengajarList({
            page,
            limit: 10,
            sort_dir: 'desc',
            search: search || undefined,
            periode_id: filterPeriode ? Number(filterPeriode) : undefined,
          }),
          getPegawaiList({ limit: 200, status_aktif: true }),
          getPeriodeHonorList({ limit: 50 }),
        ]);
        setRows(jpResult.rows);
        setMeta({ page: jpResult.meta.page, total_pages: jpResult.meta.total_pages });
        setPegawaiList(pegawaiResult.rows);
        setPeriodeList(periodeResult.rows);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Gagal memuat data total JP mengajar');
      }
    },
    [search, filterPeriode],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (row: TotalJpMengajar) => {
    setEditing(row);
    setForm({
      pegawai_sekolah_id: String(row.pegawai_sekolah_id),
      periode_id: String(row.periode_id),
      total_jp: String(row.total_jp),
    });
    setShowForm(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.pegawai_sekolah_id || !form.periode_id) {
      setError('Pegawai dan periode wajib dipilih');
      return;
    }
    const totalJp = Number(form.total_jp);
    if (!Number.isInteger(totalJp) || totalJp < 0) {
      setError('Total JP harus bilangan bulat non-negatif');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateTotalJpMengajar(editing.id, { total_jp: totalJp });
      } else {
        await createTotalJpMengajar({
          pegawai_sekolah_id: Number(form.pegawai_sekolah_id),
          periode_id: Number(form.periode_id),
          total_jp: totalJp,
        });
      }
      setShowForm(false);
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan data total JP mengajar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Hapus data total JP mengajar ini?')) return;
    try {
      await deleteTotalJpMengajar(id);
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus data total JP mengajar');
    }
  };

  return (
    <AdminLayout title="Total JP Mengajar">
      {error && <Alert type="error">{error}</Alert>}
      {showForm && canManage && (
        <Modal
          open={showForm}
          onClose={() => setShowForm(false)}
          title={editing ? 'Ubah Total JP Mengajar' : 'Tambah Total JP Mengajar'}
        >
          <form onSubmit={submit}>
            <Select
              label="Pegawai"
              value={form.pegawai_sekolah_id}
              disabled={Boolean(editing)}
              onChange={(e) => setForm({ ...form, pegawai_sekolah_id: e.target.value })}
              required
            >
              <option value="">Pilih pegawai</option>
              {pegawaiList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nip_nik} — {p.nama}
                </option>
              ))}
            </Select>
            <Select
              label="Periode"
              value={form.periode_id}
              disabled={Boolean(editing)}
              onChange={(e) => setForm({ ...form, periode_id: e.target.value })}
              required
            >
              <option value="">Pilih periode</option>
              {periodeList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.kode_periode} — {p.nama}
                </option>
              ))}
            </Select>
            <Input
              label="Total JP"
              type="number"
              min="0"
              step="1"
              value={form.total_jp}
              onChange={(e) => setForm({ ...form, total_jp: e.target.value })}
              required
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem',
                marginTop: '1rem',
              }}
            >
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                Batal
              </Button>
              <Button type="submit" disabled={saving}>
                Simpan
              </Button>
            </div>
          </form>
        </Modal>
      )}
      <Card>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Input
            placeholder="Cari nama/NIP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <Select
            value={filterPeriode}
            onChange={(e) => setFilterPeriode(e.target.value)}
            style={{ minWidth: '160px' }}
          >
            <option value="">Semua Periode</option>
            {periodeList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.kode_periode}
              </option>
            ))}
          </Select>
          {canManage && <Button onClick={openCreate}>Tambah Data</Button>}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Pegawai</Th>
              <Th>Sekolah</Th>
              <Th>Periode</Th>
              <Th>Total JP</Th>
              {canManage && <Th>Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>
                  {row.nip_nik} — {row.nama_pegawai}
                </Td>
                <Td>{row.nama_sekolah}</Td>
                <Td>
                  {row.kode_periode} — {row.nama_periode}
                </Td>
                <Td>{row.total_jp}</Td>
                {canManage && (
                  <Td>
                    <Button variant="secondary" onClick={() => openEdit(row)}>
                      Ubah
                    </Button>{' '}
                    <Button variant="danger" onClick={() => void remove(row.id)}>
                      Hapus
                    </Button>
                  </Td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <Td colSpan={canManage ? 5 : 4} style={{ textAlign: 'center' }}>
                  Tidak ada data
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        <Pagination
          page={meta.page}
          totalPages={meta.total_pages}
          onChange={(page) => void load(page)}
        />
      </Card>
    </AdminLayout>
  );
}
